import { isPresent } from '@mtna/core-ts';
import { MtnaDataType, RelationalOperator, RelationOperatorSearchText, RelationOperatorSearchTextType } from '@mtna/data-core-ts';
import { instanceOfValueExpression, LogicalComponent, SimpleLogicalComponentParser, ValueExpression } from '@mtna/data-query-ts';
import { Code, Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { RdsClassificationService, RdsVariableService } from '@rds/shared-api';
import { forkJoin, iif, Observable, of, ReplaySubject } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { MeasureOptionType } from '../../measure-select/measure-option.model';
import { MeasureSelection } from '../../measure-select/measure-selection.model';

import {
  invertNumericOperation,
  RDS_NUMERIC_OPERATION_OPTIONS,
  RdsNumericFilter,
  RdsNumericOperation,
  RdsNumericOperationOption,
} from '../../numeric-filter/numeric-filter.model';
import {
  invertTextParameter,
  RDS_TEXT_PARAMETER_OPTIONS,
  RdsTextFilter,
  RdsTextParameter,
  RdsTextParameterOption,
  RdsTextParameterSearchText,
} from '../../text-filter/text-filter.model';
import { RdsDataTypeUtil } from '../utils/data-type-util';
import { _rdsVerifyMomentJsExists } from '../utils/verify-moment-js-util';
import {
  BaseFilter,
  FilterByCodes,
  FilterByDate,
  FilterByNumber,
  FilterByText,
  FilterDisplayType,
  getRdsDateFilterIso,
  instanceOfFilterByCodes,
  instanceOfFilterByDate,
  instanceOfFilterByNumber,
  instanceOfFilterByText,
  invertDateOperation,
  invertNullOperation,
  RDS_DATE_OPERATION_OPTIONS,
  RDS_NULL_PARAMETER_OPTIONS,
  RdsDateFilter,
  RdsDateOperation,
  RdsDateOperationOption,
  RdsNullFilter,
  RdsNullParameter,
  RdsNullParameterOption,
} from '../models';

import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';
const moment = _rollupMoment || _moment;

/** Matches open and close square bracket */
const MATCH_BRACKET_GROUPS_REGEX = new RegExp('\\[(.*?)\\]', 'g');
/** Match the special characters in a LIKE statement that need to be escaped: _, %, ], and [ */
const MATCH_LIKE_SPECIAL_CHARS_REGEX = new RegExp('[_%\\[\\]]', 'g');

interface FilteredVariable {
  variableId: string;
  filters: Array<Filter>;
}

interface Filter {
  value: unknown;
  operation: RelationalOperator | null;
}

/**
 * Uses the Tokenizer to desirialize a where clause into a LogicalComponent.
 * @param where RDS where clause
 */
export function deserializeWhereClause(where: string): LogicalComponent {
  const parser = new SimpleLogicalComponentParser();
  return parser.parse(where);
}

/**
 * Async method to deserialize a LogicalComponent into the corresponding RDS Filters.
 *
 * @param logicalComponent output of deserializeWhereClause
 * @param classificationService rds service used to query classifications
 * @param variableService rds service used to query variables
 */
export function deserializeLogicalComponent(
  logicalComponent: LogicalComponent,
  classificationService: RdsClassificationService,
  variableService: RdsVariableService
): Observable<BaseFilter[]> {
  const sub = new ReplaySubject<BaseFilter[]>(1);
  const filterMap: { [id: string]: FilteredVariable } = {};

  _extractFilterIds(logicalComponent, filterMap);

  const rdsFilters$: Observable<BaseFilter>[] = [];

  // Loop through all variables being used as filters
  for (const prop in filterMap) {
    if (Object.prototype.hasOwnProperty.call(filterMap, prop)) {
      const vf: FilteredVariable = filterMap[prop];
      rdsFilters$.push(
        // Query the full Variable
        variableService.getVariable(vf.variableId).pipe(
          concatMap((variable: Variable) =>
            // If it has a classification, query the codes as well
            iif(
              () => !!variable.classificationUri,
              classificationService.getCodes(variable.classificationUri as string).pipe(map((codes) => ({ variable, codes }))),
              of({ variable, codes: [] })
            )
          ),
          map(({ variable, codes }) => {
            // Null is a reserved filter, handle it separately
            const filters = vf.filters.filter((f) => f.value !== 'NULL');
            // Determine if there is a null filter
            const nullFilter: Filter | undefined = vf.filters.find((f) => f.value === 'NULL');
            let rdsNullFilter: RdsNullFilter | undefined;
            if (nullFilter) {
              const nullParameter: RdsNullParameter = nullFilter?.operation === 'EQ' ? '=NULL' : '!=NULL';
              const nullParameterOption: RdsNullParameterOption = RDS_NULL_PARAMETER_OPTIONS.find(
                (o) => o.type === nullParameter
              ) as RdsNullParameterOption;
              rdsNullFilter = new RdsNullFilter(nullParameterOption);
            }

            if (variable.classificationUri) {
              // Code filter
              return new FilterByCodes(
                variable,
                filters.map((f) => codes.find((c) => c.codeValue === f.value) as Code),
                rdsNullFilter,
                filters[0].operation === 'NE'
              );
            } else if (RdsDataTypeUtil.simplifyMtnaDataType(variable.dataType as MtnaDataType) === 'TEXT') {
              // Text filter
              return new FilterByText(
                variable,
                filters.map((f) => _deserializeRdsTextParameterOption(f)),
                rdsNullFilter
              );
            } else if (RdsDataTypeUtil.simplifyMtnaDataType(variable.dataType as MtnaDataType) === 'NUMERIC') {
              // Numeric filter
              return new FilterByNumber(
                variable,
                filters.map(
                  (f) =>
                    new RdsNumericFilter(
                      f.value as number,
                      RDS_NUMERIC_OPERATION_OPTIONS.find((o) => o.type === f.operation) as RdsNumericOperationOption
                    )
                ),
                rdsNullFilter
              );
            } else if (variable.dataType === 'DATE') {
              // Ensure moment js was added to the application correctly
              _rdsVerifyMomentJsExists();

              // TODO support TIME and TIMESTAMP
              // Date filter
              return new FilterByDate(
                variable,
                filters.map(
                  (f) =>
                    new RdsDateFilter(
                      moment(f.value as Date | string),
                      RDS_DATE_OPERATION_OPTIONS.find((o) => o.type === f.operation) as RdsDateOperationOption
                    )
                ),
                rdsNullFilter
              );
            } else {
              // We have a problem if we get here
              return new BaseFilter(variable);
            }
          })
        )
      );
    }
  }

  forkJoin(rdsFilters$).subscribe(
    (baseFilters) => sub.next(baseFilters),
    (err) => sub.error(err),
    () => sub.complete()
  );

  return sub.asObservable();
}

/**
 * Serialize a single measure into a url param as `name:func(variableId)`
 * @param measure the measure to serialize
 * @return the string representation of the measure to use as URL parameters
 */
export function serializeMeasureToUrlParam<V extends Variable | VariableSummary = Variable>(measure: MeasureSelection<V>): string {
  if (!measure) {
    return '';
  }
  const nameOfComputedVariabe = measure.variable ? measure.variable.name : measure.measureType;
  const variableBeingMeasured = measure.variable ? measure.variable.id : '*';
  return `${nameOfComputedVariabe}:${measure.measureType}(${variableBeingMeasured})`;
}

/**
 * Deserialize a single measure from a url param as `name:func(variableId)` to a `MeasureSelection`
 * @param value string to deserialize back into a measure
 * @return the `MeasureSelection` object deserialized from the given string
 */
export function deserializeMeasureFromUrlParam<V extends Variable | VariableSummary = Variable>(
  value: string,
  variables: Array<V>
): MeasureSelection<V> | undefined {
  if (!value) {
    return undefined;
  }
  const splitOnColon = value.split(':');
  if (splitOnColon.length !== 2) {
    return undefined;
  }

  const func = splitOnColon[1];
  const indexOfOpenParenthesis = func.indexOf('(');
  const measureType = func.slice(0, indexOfOpenParenthesis).toUpperCase() as MeasureOptionType;
  const variableIdBeingMeasured = func.slice(indexOfOpenParenthesis + 1, func.length - 1);
  const variable: V | undefined = variableIdBeingMeasured === '*' ? undefined : variables.find((v) => v.id === variableIdBeingMeasured);
  return new MeasureSelection<V>(measureType, variable);
}

/**
 * Generate the RDS api where clause for the select query
 * @param filterState Filter display type
 * @param appliedFilters applied filters
 * @returns where clause
 */
export function serializeWhereClause(filterState: FilterDisplayType, appliedFilters: BaseFilter[]): string | null {
  return filterState === 'OFF' || !appliedFilters || !appliedFilters.length
    ? null
    : appliedFilters
        .map((bf) => {
          if (instanceOfFilterByCodes(bf)) {
            // For each variable with codes, create a string that joins them, i.e:
            // (STATE=01 OR STATE=02 OR STATE=03) if the filter state is ON or
            // (STATE!=01 AND STATE!=02 AND STATE!=03) if the filter state is INVERTED
            let joinedFilters = bf.codes
              .map((code) => {
                let value = `${bf.variable.id}`;
                switch (filterState) {
                  case 'ON':
                    value += `${bf.excludeCodes ? '!=' : '='}`;
                    break;
                  case 'INVERTED':
                    value += `${bf.excludeCodes ? '=' : '!='}`;
                    break;
                }
                value += `${code.codeValue}`;
                return value;
              })
              .join(_getFilterJoinOperator(filterState, bf.excludeCodes));

            // Add null filter if it exists
            joinedFilters = _joinNullFilter(bf, joinedFilters, filterState);
            return `(${joinedFilters})`;
          } else if (instanceOfFilterByNumber(bf)) {
            return `(${_serializeNumericFilters(bf, filterState === 'INVERTED')})`;
          } else if (instanceOfFilterByText(bf)) {
            let joinedFilters = bf.textFilters
              .map((t) => {
                // We can safely disable non-null-assertion due to instanceOfFilterByText
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const parameter: RdsTextParameter = t.parameter!.type;
                const operator: RelationOperatorSearchTextType =
                  filterState === 'ON' ? RdsTextParameterSearchText[parameter] : RdsTextParameterSearchText[invertTextParameter(parameter)];
                return `${bf.variable.id}${operator}${_wrapTextValue(t.text, parameter)}`;
              })
              .join(_getFilterJoinOperator(filterState));
            // Add null filter if it exists
            joinedFilters = _joinNullFilter(bf, joinedFilters, filterState);
            return `(${joinedFilters})`;
          } else if (instanceOfFilterByDate(bf)) {
            return `(${_serializeDateFilters(bf, filterState === 'INVERTED')})`;
          } else {
            throw new Error(`Unknown filter type: ${JSON.stringify(bf)}`);
          }
        })
        // AND join all the variable clauses, i.e.:
        // (STATE=01 OR STATE=02 OR STATE=03) AND (REGION=01 || REGION=02)
        .join(' AND ');
}

/**
 *
 * @param filter the filter to deserialize
 * @returns the corresponding RdsTextParameterOption
 */
function _deserializeRdsTextParameterOption(filter: Filter): RdsTextFilter {
  if (typeof filter.value !== 'string') {
    throw new Error(
      `[deserializeRdsTextParameterOption] the filter value is not a string.\nFilter operation: ${filter.operation}\nFilter value: ${filter.value}`
    );
  }
  if (filter.operation === 'LIKE' || filter.operation === 'NOT_LIKE') {
    const isNot = filter.operation === 'NOT_LIKE';
    const startsWithWildcard = filter.value.startsWith('%');
    const endsWithWildcard = filter.value.endsWith('%');

    // Determine RdsTextParameter
    let parameter: RdsTextParameter | undefined;
    if (startsWithWildcard && endsWithWildcard) {
      parameter = isNot ? 'NOT_CONTAINS' : 'CONTAINS';
    } else if (startsWithWildcard) {
      parameter = isNot ? 'NOT_ENDS_WITH' : 'ENDS_WITH';
    } else if (endsWithWildcard) {
      parameter = isNot ? 'NOT_STARTS_WITH' : 'STARTS_WITH';
    } else {
      throw new Error(
        `[deserializeRdsTextParameterOption] could not determine RdsTextParameter.\nFilter operation: ${filter.operation}\nFilter value: ${filter.value}`
      );
    }

    // Extract filter text value
    let filterTextValue: string = filter.value;
    if (startsWithWildcard) {
      // Remove leading wildcard (percent sign)
      filterTextValue = filterTextValue.substring(1);
    }

    if (endsWithWildcard) {
      // Remove trailing wildcard (percent sign)
      filterTextValue = filterTextValue.substring(0, filterTextValue.length - 1);
    }

    // Unescape any special characters that were originally escaped
    filterTextValue = _unescapeLikeQuery(filterTextValue);

    return new RdsTextFilter(filterTextValue, RDS_TEXT_PARAMETER_OPTIONS.find((o) => o.type === parameter) as RdsTextParameterOption);
  } else {
    return new RdsTextFilter(
      filter.value as string,
      RDS_TEXT_PARAMETER_OPTIONS.find((o) => o.type === filter.operation) as RdsTextParameterOption
    );
  }
}

/**
 * Returns a string separator to insert between two numeric/date filters.
 * @param a RdsNumericFilter | RdsDateFilter on left of separator
 * @param b RdsNumericFilter | RdsDateFilter on right of separator
 * @param invert Whether the filters are inverted
 */
function _determineSeparator(a: RdsNumericFilter | RdsDateFilter, b: RdsNumericFilter | RdsDateFilter, invert = false): string {
  if (!isPresent(a.operation) || !isPresent(b.operation) || !isPresent(a.value) || !isPresent(b.value)) {
    return '';
  }
  const op1 = invert ? ' OR ' : ' AND ';
  const op2 = invert ? ' AND ' : ' OR ';
  const noEq: string = (a.operation.type + b.operation.type).replace(/=/g, '');
  if (noEq === '><') {
    return a.value < b.value ? op1 : op2;
  } else {
    // The string must equal '<>'
    return a.value > b.value ? op1 : op2;
  }
}

/**
 * Escape the special characters in a like statement: _, %, ], and [
 * by wraping these characters in square brackets.
 * @param value string to escape
 * @returns escaped string
 */
function _escapeLikeQuery(value: string): string {
  return value.replace(MATCH_LIKE_SPECIAL_CHARS_REGEX, '[$&]');
}

function _extractFilterIds(obj: LogicalComponent | ValueExpression, filterMap: { [id: string]: FilteredVariable }) {
  if (instanceOfValueExpression(obj)) {
    if (filterMap[obj.variableId]) {
      // If the variable already exists in the map, add another filter to it
      filterMap[obj.variableId] = {
        variableId: obj.variableId,
        filters: [...filterMap[obj.variableId].filters, { value: obj.value, operation: obj.relationalOperator }],
      };
    } else {
      // If the variable isn't in the map, add it for the first time
      filterMap[obj.variableId] = { variableId: obj.variableId, filters: [{ value: obj.value, operation: obj.relationalOperator }] };
    }
  } else {
    // If it is a LogicalComponent, keep recursing
    for (const prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop) && prop !== 'logicalOperator') {
        const subObj = obj[prop as keyof LogicalComponent];
        _extractFilterIds(subObj, filterMap);
      }
    }
  }
}

/**
 * Returns a string separator for equality signs.
 * @param op RdsNumericOperation  associated with a separator; Must be `=` or `!=`
 */
function _getEqualitySeparator(op: RdsNumericOperation | RdsDateOperation): string {
  switch (op) {
    case 'EQ':
      return ` OR `;
    case 'NE':
      return ` AND `;
    default:
      return '';
  }
}

/**
 * Whether to return the opposite value that would be applied by the given state value
 * @param state State of the filter, whether on or inverted
 * @param invert Whether to invert the value, in relation to the state
 */
function _getFilterJoinOperator(state: 'ON' | 'INVERTED', invert = false): string {
  switch (state) {
    case 'ON':
      return invert ? ' AND ' : ' OR ';
    case 'INVERTED':
      return invert ? ' OR ' : ' AND ';
  }
}

/**
 * If a null filter exists, concatentate it on the filter string
 * @returns a new filter string with the null filter
 */
function _joinNullFilter(bf: BaseFilter<Variable | VariableSummary>, joinedFilters: string, filterState: 'ON' | 'INVERTED') {
  let updatedJoinedFilters = joinedFilters;
  if (bf.nullFilter && bf.nullFilter.parameter?.type !== 'NOT_APPLIED') {
    if (joinedFilters) {
      updatedJoinedFilters += filterState === 'ON' ? ' OR ' : ' AND ';
    }
    updatedJoinedFilters += `${bf.variable.id}${
      filterState === 'ON' ? bf.nullFilter.parameter.type : invertNullOperation(bf.nullFilter.parameter.type)
    }`;
  }
  return updatedJoinedFilters;
}

/**
 * Returns string combination of all date filter parameters
 * @param bf FilterByDate containing all date filters
 * @param invert Whether the filters inverted
 */
function _serializeDateFilters<V extends Variable | VariableSummary>(bf: FilterByDate<V>, invert = false): string {
  // We can safely use the non-null-assertion in this method
  // since we filter out null operations here
  const eqArray: Array<RdsDateFilter> = bf.dateFilters.filter(
    (n) => !!n.operation && (n.operation.type === 'EQ' || n.operation.type === 'NE')
  );
  const compareArray: Array<RdsDateFilter> = bf.dateFilters.filter(
    (n) => !!n.operation && n.operation.type !== 'EQ' && n.operation.type !== 'NE'
  );
  const eqString = eqArray
    .map((n, i, self) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const operation: RdsDateOperation = n.operation!.type;
      const operator: RelationOperatorSearchTextType = !invert
        ? RelationOperatorSearchText[operation]
        : RelationOperatorSearchText[invertDateOperation(operation)];
      return `${bf.variable.id}${operator}${getRdsDateFilterIso(n)}${
        i < self.length - 1 ? (!invert ? _getEqualitySeparator(operation) : _getEqualitySeparator(invertDateOperation(operation))) : ''
      }`;
    })
    .join('')
    .concat(
      !!eqArray[0] && !!compareArray.length
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          _getEqualitySeparator(!invert ? eqArray[0].operation!.type : invertDateOperation(eqArray[0].operation!.type))
        : ''
    );

  const cString = compareArray
    .map((n, i, self) => {
      const hasNext = !!self[i + 1];
      const prefix = i % 2 === 0 && hasNext ? '(' : '';
      let suffix = i % 2 !== 0 ? ')' : '';
      if (i % 2 !== 0 && hasNext) {
        suffix += ' OR ';
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const operation: RdsDateOperation = n.operation!.type;
      const operator: RelationOperatorSearchTextType = !invert
        ? RelationOperatorSearchText[operation]
        : RelationOperatorSearchText[invertDateOperation(operation)];
      return `${prefix}${bf.variable.id}${operator}${getRdsDateFilterIso(n)}${
        i % 2 === 0 && hasNext ? _determineSeparator(n, self[i + 1], invert) : ''
      }${suffix}`;
    })
    .join('');

  let joinedFilters = eqString + cString;
  // Add null filter if it exists
  joinedFilters = _joinNullFilter(bf, joinedFilters, invert ? 'INVERTED' : 'ON');
  return joinedFilters;
}

/**
 * Returns string combination of all numeric filter parameters
 * @param bf FilterByNumber containing all numeric filters
 * @param invert Whether the filters are normal or inverted
 */
function _serializeNumericFilters<V extends Variable | VariableSummary>(bf: FilterByNumber<V>, invert = false): string {
  // We can safely use the non-null-assertion in this method
  // since we filter out null operations here
  const eqArray: Array<RdsNumericFilter> = bf.numericFilters.filter(
    (n) => !!n.operation && (n.operation.type === 'EQ' || n.operation.type === 'NE')
  );
  const compareArray: Array<RdsNumericFilter> = bf.numericFilters.filter(
    (n) => !!n.operation && n.operation.type !== 'EQ' && n.operation.type !== 'NE'
  );
  const eqString = eqArray
    .map((n, i, self) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const operation: RdsNumericOperation = n.operation!.type;
      const operator: RelationOperatorSearchTextType = !invert
        ? RelationOperatorSearchText[operation]
        : RelationOperatorSearchText[invertNumericOperation(operation)];
      return `${bf.variable.id}${operator}${n.value}${
        i < self.length - 1 ? (!invert ? _getEqualitySeparator(operation) : _getEqualitySeparator(invertNumericOperation(operation))) : ''
      }`;
    })
    .join('')
    .concat(
      !!eqArray[0] && !!compareArray.length
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          _getEqualitySeparator(!invert ? eqArray[0].operation!.type : invertNumericOperation(eqArray[0].operation!.type))
        : ''
    );

  const cString = compareArray
    .map((n, i, self) => {
      const hasNext = !!self[i + 1];
      const prefix = i % 2 === 0 && hasNext ? '(' : '';
      let suffix = i % 2 !== 0 ? ')' : '';
      if (i % 2 !== 0 && hasNext) {
        suffix += ' OR ';
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const operation: RdsNumericOperation = n.operation!.type;
      const operator: RelationOperatorSearchTextType = !invert
        ? RelationOperatorSearchText[operation]
        : RelationOperatorSearchText[invertNumericOperation(operation)];
      return `${prefix}${bf.variable.id}${operator}${n.value}${
        i % 2 === 0 && hasNext ? _determineSeparator(n, self[i + 1], invert) : ''
      }${suffix}`;
    })
    .join('');

  let joinedFilters = eqString + cString;
  // Add null filter if it exists
  joinedFilters = _joinNullFilter(bf, joinedFilters, invert ? 'INVERTED' : 'ON');
  return joinedFilters;
}

/**
 * Unescapes the special characters in a like statement: _, %, ], and [
 * that have been wrapped in square brackets
 * @param value string to unescape
 * @returns escaped string
 */
function _unescapeLikeQuery(value: string): string {
  return value.replace(MATCH_BRACKET_GROUPS_REGEX, '$1');
}

function _wrapTextValue(value: string | null | undefined, param: RdsTextParameter): string {
  let newValue = value || '';
  if (newValue.includes('"')) {
    newValue = newValue.replace(/"/g, `\\"`);
  }
  switch (param) {
    case 'CONTAINS':
    case 'NOT_CONTAINS':
      // add wildcards before and after text
      return `"%${_escapeLikeQuery(newValue)}%"`;
    case 'STARTS_WITH':
    case 'NOT_STARTS_WITH':
      // add wildard after text
      return `"${_escapeLikeQuery(newValue)}%"`;
    case 'ENDS_WITH':
    case 'NOT_ENDS_WITH':
      // add wildcard before text
      return `"%${_escapeLikeQuery(newValue)}"`;
    default:
      return `"${newValue}"`;
  }
}
